/**
 * NewsletterForm — Public-facing email capture form.
 * Submits to /api/newsletter/subscribe.
 * Source label tags where the subscriber came from (e.g. "footer", "popup").
 */
import React, { useState } from "react";
import { Mail, Check } from "lucide-react";
import api from "@/lib/api";

export default function NewsletterForm({ source = "footer", variant = "default" }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null); // 'ok' | 'already' | 'err'
    const [errMsg, setErrMsg] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setStatus(null);
        try {
            const r = await api.post("/newsletter/subscribe", { email: email.trim(), source });
            setStatus(r.data.already_subscribed ? "already" : "ok");
            if (!r.data.already_subscribed) setEmail("");
        } catch (e) {
            setStatus("err");
            setErrMsg(e.response?.data?.detail || "Bir hata oluştu. Lütfen tekrar deneyin.");
        } finally { setBusy(false); }
    };

    if (status === "ok") {
        return (
            <div data-testid="newsletter-success" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
                <Check className="h-4 w-4 flex-shrink-0" />
                <span>Aboneliğiniz onaylandı! Kampanyalardan ilk siz haberdar olacaksınız.</span>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="newsletter-form" className={variant === "inline" ? "flex flex-col sm:flex-row gap-2" : "space-y-2"}>
            <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    data-testid="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="E-posta adresiniz"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 text-sm focus:outline-none focus:border-white/50"
                />
            </div>
            <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={busy}
                className="px-5 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-bold disabled:opacity-50 whitespace-nowrap"
            >
                {busy ? "..." : "Abone Ol"}
            </button>
            {status === "already" && <div className="text-xs text-amber-300 sm:basis-full">Bu e-posta zaten kayıtlı, teşekkürler!</div>}
            {status === "err" && <div className="text-xs text-red-300 sm:basis-full">{errMsg}</div>}
        </form>
    );
}
